/* ============================================================
   UNDER THE CROSS — Shared chrome script (Shopify-adapted)
   Surgical port of Under_The_Cross_Handoff/01-design-preview/
     assets/js/utc.js. Modules dropped because Shopify-side
     equivalents already exist (Alpine.store):
       - Cart drawer + persistent cart      → Alpine.store('cart') with Ajax
       - Search drawer                      → Alpine.store('search') with Predictive Search
       - Sidebar / mobile menu              → Alpine.store('nav')
       - Add-to-cart wiring                 → Alpine.store('cart').add()
       - Product card data injection        → Liquid renders server-side
       - Flagship status badge injection    → Liquid renders server-side
       - Legal footer column injector       → footer.liquid renders directly
       - Cinzel Decorative font ensure      → snippets/font-face.liquid loads it
   Modules kept (verbatim where possible, adapted for Shopify
   URLs where needed):
       - Reveal observer (one-shot fade-up on .reveal)
       - Scroll progress red line
       - Pin for Prayer (localStorage utc_pinned)
       - Toast helper (#toast)
       - Active-nav-link highlighter (rewritten for Shopify paths)
       - Account icon routing (reads <meta name="utc-customer-id">)
       - Mobile sidebar brand-mark injector
       - Mobile nav notch injector
       - Footer accordion on mobile
   Each module is wrapped in its own IIFE for fault isolation.
   ============================================================ */
(() => {
  'use strict';

  window.UTC = window.UTC || {};

  const root = document.documentElement;

  /* ── Locale ──────────────────────────────────────────────
     data-lang is set server-side on <html> (gr on /, en on /en) by
     theme.liquid. The old client-side toggle was retired with the native
     /localization switcher; reading stale localStorage would fight the
     server-rendered language, so it's gone. _t() picks one language for the
     few JS-injected UI strings (toasts) that Shopify can't translate. */
  const _lang = root.getAttribute('data-lang') || 'gr';
  const _t = (en, gr) => (_lang === 'gr' ? gr : en);

  /* ── Reveal observer (one-shot fade-up) ─────────────────── */
  (() => {
    const revealIO = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (en.isIntersecting) {
          en.target.classList.add('is-in');
          revealIO.unobserve(en.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.reveal').forEach(el => revealIO.observe(el));
    window.UTC.observeReveal = (el) => { if (el) revealIO.observe(el); };
  })();

  /* ── Scroll progress red line ───────────────────────────── */
  (() => {
    const bar = document.getElementById('scrollProgress');
    if (!bar) return;
    let raf = 0;
    const update = () => {
      const docEl = document.documentElement;
      const h = docEl.scrollHeight - docEl.clientHeight;
      const p = h > 0 ? (docEl.scrollTop / h) : 0;
      bar.style.height = (p * 100) + 'vh';
      raf = 0;
    };
    window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    update();
  })();

  /* ── Pin for Prayer ──────────────────────────────────────
     Stored client-side in `utc_pinned` (JSON array of product
     handles). In Phase G we mirror to customer.metafields.utc.pinned
     via a small bridge for logged-in customers. ───────────── */
  (() => {
    function getPinned() {
      try { return JSON.parse(localStorage.getItem('utc_pinned') || '[]'); }
      catch (e) { return []; }
    }
    function setPinned(arr) {
      try { localStorage.setItem('utc_pinned', JSON.stringify(arr)); } catch (e) {}
    }
    function isPinned(id) { return getPinned().includes(id); }
    function togglePin(id, btn) {
      const pinned = getPinned();
      const i = pinned.indexOf(id);
      if (i >= 0) {
        pinned.splice(i, 1);
        setPinned(pinned);
        btn && btn.classList.remove('is-pinned');
        showToast(_t('Removed from Awaiting Discernment.', 'Αφαιρέθηκε από τη λίστα «Σε Αναμονή Διάκρισης».'));
        return false;
      }
      pinned.push(id);
      setPinned(pinned);
      if (btn) {
        btn.classList.add('is-pinned', 'flicker');
        setTimeout(() => btn.classList.remove('flicker'), 700);
      }
      showToast(
        '<span class="glyph-cross" aria-hidden="true"></span>' + _t('Pinned to Awaiting Discernment.', 'Καρφιτσώθηκε στη λίστα «Σε Αναμονή Διάκρισης».'),
        _t('View', 'Δες')
      );
      return true;
    }
    window.UTC.togglePin = togglePin;
    window.UTC.isPinned = isPinned;
    window.UTC.getPinned = getPinned;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pin]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      togglePin(btn.getAttribute('data-pin'), btn);
    });

    // Paint initial pinned state on any [data-pin] buttons in the DOM
    const pinned = getPinned();
    document.querySelectorAll('[data-pin]').forEach(btn => {
      btn.classList.toggle('is-pinned', pinned.includes(btn.getAttribute('data-pin')));
    });
  })();

  /* ── Toast helper ───────────────────────────────────────── */
  let toastTimer;
  function showToast(html, actionHtml) {
    const toastEl = document.getElementById('toast');
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.innerHTML = html + (actionHtml ? '<span class="toast__action">' + actionHtml + '</span>' : '');
    toastEl.classList.add('show');
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), 3200);
  }
  window.UTC.showToast = showToast;

  /* ── Active nav link based on Shopify pathname ──────────── */
  (() => {
    const path = (location.pathname || '').toLowerCase();
    document.querySelectorAll('[data-nav]').forEach(a => {
      const key = a.getAttribute('data-nav');
      if (key === 'shop' && (path.startsWith('/collections') || path.startsWith('/products'))) a.classList.add('is-active');
      if (key === 'word' && path.includes('/pages/the-word')) a.classList.add('is-active');
      if (key === 'charity' && path.includes('/pages/charity')) a.classList.add('is-active');
      if (key === 'about' && path.includes('/pages/about')) a.classList.add('is-active');
      if (key === 'home' && (path === '/' || path === '/en' || path === '/en/' || /^\/[a-z]{2}\/?$/.test(path))) a.classList.add('is-active');
    });
  })();

  /* ── Account icon routing ───────────────────────────────────
     Reads <meta name="utc-customer-id"> rendered server-side by
     theme.liquid. Empty / missing → not logged in. ──────────── */
  (() => {
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[aria-label="Account"]');
      if (!t || t.tagName === 'A') return;  // real <a href> wins
      e.preventDefault();
      const meta = document.querySelector('meta[name="utc-customer-id"]');
      const id = meta ? (meta.getAttribute('content') || '').trim() : '';
      window.location.href = id ? '/account' : '/account/login';
    });
  })();

  /* ── Cookie consent ─────────────────────────────────────────
     Handled by Shopify's native consent banner (Admin → Settings →
     Customer privacy). A custom JS banner used to live here; it was
     removed so the store shows only one banner. ──────────────── */

  /* ── Mobile sidebar brand-mark: inject a centred block at the top of
        every .sidebar — big logo + three-line stacked wordmark
        (Under / the / Cross), all centered. ────────────────── */
  (() => {
    document.querySelectorAll('.sidebar').forEach(s => {
      if (s.querySelector('.sidebar__brand-mark')) return;
      const head = s.querySelector('.sidebar__head');
      const existingLogo = s.querySelector('.sidebar__brand img');
      const src = existingLogo
        ? existingLogo.getAttribute('src')
        : '';
      if (!src) return;
      const mark = document.createElement('div');
      mark.className = 'sidebar__brand-mark';
      mark.innerHTML =
        '<img class="sidebar__brand-mark-logo" src="' + src + '" alt="Under The Cross" ' +
          'width="72" height="72" style="width:72px;height:72px;object-fit:contain;display:block;margin:0 auto 16px;" />' +
        '<span class="sidebar__brand-mark-line sidebar__brand-mark-line--under">Under</span>' +
        '<span class="sidebar__brand-mark-line sidebar__brand-mark-line--the">the</span>' +
        '<span class="sidebar__brand-mark-line sidebar__brand-mark-line--cross">Cross</span>';
      if (head) head.insertAdjacentElement('afterend', mark);
      else s.insertBefore(mark, s.firstChild);
    });
  })();

  /* ── Mobile nav notch + bar injector ──────────────────────
     Adds the rectangular white bar + bottom semicircle bulge
     that holds the circular logo on .nav__mobile. ────────── */
  (() => {
    document.querySelectorAll('.nav__mobile').forEach(nm => {
      if (nm.querySelector('.nav__mobile-bar')) return;
      const bar = document.createElement('div');
      bar.className = 'nav__mobile-bar';
      bar.setAttribute('aria-hidden', 'true');
      const notch = document.createElement('div');
      notch.className = 'nav__mobile-notch';
      notch.setAttribute('aria-hidden', 'true');
      nm.insertBefore(notch, nm.firstChild);
      nm.insertBefore(bar, nm.firstChild);
    });
  })();

  /* ── Footer accordion on mobile ─────────────────────────── */
  (() => {
    const mql = window.matchMedia('(max-width: 768px)');
    function apply() {
      const cols = document.querySelectorAll('.foot__col');
      if (!cols.length) return;
      const mobile = mql.matches;
      cols.forEach((col, i) => {
        const h4 = col.querySelector('h4');
        if (!h4) return;
        if (mobile) {
          if (col.classList.contains('foot__col--ac')) return;
          col.classList.add('foot__col--ac');
          if (i === 0) col.classList.add('is-open');
          h4.setAttribute('role', 'button');
          h4.setAttribute('tabindex', '0');
          h4.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');
          h4.addEventListener('click', () => {
            const open = col.classList.toggle('is-open');
            h4.setAttribute('aria-expanded', open ? 'true' : 'false');
          });
          h4.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); h4.click(); }
          });
        } else {
          col.classList.remove('foot__col--ac', 'is-open');
          h4.removeAttribute('role');
          h4.removeAttribute('tabindex');
          h4.removeAttribute('aria-expanded');
        }
      });
    }
    apply();
    try { mql.addEventListener('change', apply); } catch (_) { mql.addListener(apply); }
  })();

})();
